import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import relativeTime from 'dayjs/plugin/relativeTime';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// Register plugins
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.locale('vi');

/**
 * Date formatting utility for consistent Vietnamese date display
 * Uses dayjs with Vietnamese locale
 */

/**
 * Format a date string to Vietnamese locale format (DD/MM/YYYY)
 * @param dateStr - ISO date string or timestamp
 * @returns Formatted date string or '-' if invalid
 */
export const formatDate = (dateStr?: string | number | Date | null): string => {
  if (!dateStr) return '-';
  const date = dayjs(dateStr);
  if (!date.isValid()) return '-';
  return date.locale('vi').format('DD/MM/YYYY');
};

/**
 * Format a date string to Vietnamese locale format with time (DD/MM/YYYY HH:mm)
 * @param dateStr - ISO date string or timestamp
 * @returns Formatted datetime string or '-' if invalid
 */
export const formatDateTime = (dateStr?: string | number | Date | null): string => {
  if (!dateStr) return '-';
  const date = dayjs(dateStr);
  if (!date.isValid()) return '-';
  return date.locale('vi').format('DD/MM/YYYY HH:mm');
};

/**
 * Format a date string with full datetime and seconds (DD/MM/YYYY HH:mm:ss)
 * @param dateStr - ISO date string or timestamp
 * @returns Formatted datetime string or '-' if invalid
 */
export const formatDateTimeSeconds = (dateStr?: string | number | Date | null): string => {
  if (!dateStr) return '-';
  const date = dayjs(dateStr);
  if (!date.isValid()) return '-';
  return date.locale('vi').format('DD/MM/YYYY HH:mm:ss');
};

/**
 * Get relative time from now (e.g., "5 phút trước", "2 ngày trước")
 * @param dateStr - ISO date string or timestamp
 * @returns Relative time string or '-' if invalid
 */
export const formatRelativeTime = (dateStr?: string | number | Date | null): string => {
  if (!dateStr) return '-';
  const date = dayjs(dateStr);
  if (!date.isValid()) return '-';
  return date.locale('vi').fromNow();
};

/**
 * Format a date string to ISO format (YYYY-MM-DD)
 * @param dateStr - ISO date string or timestamp
 * @returns ISO date string or '-' if invalid
 */
export const formatISODate = (dateStr?: string | number | Date | null): string => {
  if (!dateStr) return '-';
  const date = dayjs(dateStr);
  if (!date.isValid()) return '-';
  return date.format('YYYY-MM-DD');
};

export default {
  formatDate,
  formatDateTime,
  formatDateTimeSeconds,
  formatRelativeTime,
  formatISODate,
};
